import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Sparkles, Rocket, TrendingUp, Star, Globe, Users as UsersIcon, Award as AwardIcon, Trophy, Scale, Lightbulb, Target, Heart, Users } from 'lucide-react';

export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(245,158,11,0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(245,158,11,0.15),transparent_50%)]" />
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="inline-flex items-center gap-2 bg-amber-500 text-black px-5 py-2 text-sm font-bold mb-8 rounded-full">
            <Sparkles className="h-4 w-4" />
            OUR STORY
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight mb-6 leading-tight">
            Celebrating Excellence,<br />
            <span className="text-amber-400">Shaping Tomorrow</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            For over two decades, we have been at the forefront of recognizing and celebrating the extraordinary talent that shapes the exhibition design industry worldwide.
          </p>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-20 bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
                Our Mission
              </h2>
              <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                <p>
                  We exist to <strong className="text-gray-900">honor the architects of experience</strong> — the designers, builders, and visionaries who transform empty spaces into captivating brand stories. Every year, countless professionals pour their creativity, sweat, and passion into creating exhibition stands that inspire, engage, and leave lasting impressions.
                </p>
                <p>
                  Yet, too often, their extraordinary work goes unrecognized beyond the brief moments of an exhibition. We believe these talents deserve more. They deserve a <strong className="text-gray-900">global stage</strong>, international recognition, and the prestige that comes with being acknowledged among the world's best.
                </p>
                <p>
                  Stand Vision Awards was born from this conviction — <strong className="text-gray-900">to create a platform where excellence is celebrated, innovation is rewarded, and talent is elevated</strong> to its rightful place in the spotlight.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop"
                alt="Exhibition presentation"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Two Decades of Dedication */}
      <section className="py-20 bg-gray-50">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Two Decades of Dedication
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From humble beginnings to becoming the industry's most prestigious recognition platform
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 bg-white border shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mb-6">
                  <Rocket className="h-8 w-8 text-blue-600" />
                </div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">2000s</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">The Beginning</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Started as a regional initiative to recognize exceptional exhibition design. Our founders witnessed incredible talent being overlooked and knew something had to change.
                </p>
              </div>
            </Card>

            <Card className="p-8 bg-white border shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">2010s</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Global Expansion</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Expanded across continents, establishing partnerships with leading exhibition venues, trade show organizers, and design institutions in over 50 countries.
                </p>
              </div>
            </Card>

            <Card className="p-8 bg-white border shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mb-6">
                  <Star className="h-8 w-8 text-amber-600" />
                </div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">TODAY</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Industry Standard</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Now recognized as the world's premier exhibition design awards, setting the benchmark for excellence and inspiring the next generation of designers worldwide.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Why We Do This */}
      <section className="py-20 bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-8 text-center">
            Why We Do This
          </h2>
          <div className="max-w-4xl mx-auto space-y-4 text-base text-gray-700 leading-relaxed">
            <p>
              Behind every award-winning stand is a team that has <strong className="text-gray-900">worked tirelessly</strong> — late nights perfecting designs, overcoming logistical challenges, innovating under pressure, and bringing bold visions to life within tight deadlines and budgets.
            </p>
            <p>
              We've witnessed designers in Frankfurt, builders in Dubai, creative directors in São Paulo, and project managers in Singapore — all pushing the boundaries of what's possible in exhibition design. These are the <strong className="text-gray-900">unsung heroes</strong> of the trade show industry.
            </p>
            <p>
              Through Stand Vision Awards, we provide them with what they truly deserve: <strong className="text-gray-900">international recognition, career-defining credentials, and a community</strong> that celebrates their craft. We take immense pride in knowing that our platform has launched careers, secured major projects, and brought well-deserved spotlight to thousands of talented professionals.
            </p>
            <p className="text-lg font-bold text-gray-900">
              This is not just an awards program. This is our commitment to the industry we love.
            </p>
          </div>
        </div>
      </section>

      {/* Global Impact */}
      <section className="py-20 bg-gray-50">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Global Impact
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our reach extends across continents, touching lives and careers in every corner of the exhibition design world
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <Globe className="h-12 w-12 text-amber-600" />
              </div>
              <div className="text-5xl font-extrabold text-gray-900 mb-2">50+</div>
              <div className="text-sm font-semibold text-gray-600">Countries Represented</div>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <UsersIcon className="h-12 w-12 text-amber-600" />
              </div>
              <div className="text-5xl font-extrabold text-gray-900 mb-2">500+</div>
              <div className="text-sm font-semibold text-gray-600">Annual Participants</div>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <AwardIcon className="h-12 w-12 text-amber-600" />
              </div>
              <div className="text-5xl font-extrabold text-gray-900 mb-2">20+</div>
              <div className="text-sm font-semibold text-gray-600">Expert Jury Members</div>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <Trophy className="h-12 w-12 text-amber-600" />
              </div>
              <div className="text-5xl font-extrabold text-gray-900 mb-2">8</div>
              <div className="text-sm font-semibold text-gray-600">Award Categories</div>
            </div>
          </div>

          <p className="text-base text-gray-700 leading-relaxed text-center max-w-4xl mx-auto">
            From the bustling trade shows of Asia to the design capitals of Europe, from emerging markets in Africa to the innovation hubs of the Americas — Stand Vision Awards connects, celebrates, and elevates the brilliant minds shaping the future of exhibition design. Every winning project inspires thousands more. Every recognized designer paves the way for the next generation.
          </p>
        </div>
      </section>

      {/* Our Commitment to You */}
      <section className="py-20 bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Our Commitment to You
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We don't just run an awards program. We're building a legacy of excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Scale className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Transparent Judging</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Every submission is evaluated by our independent international jury panel using clear, objective criteria. No politics, no favoritism - just pure merit-based recognition.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Industry Advancement</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    We reinvest in the community through educational initiatives, design resources, and networking opportunities that strengthen the entire exhibition design ecosystem.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Star className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Celebrating Diversity</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    From massive pavilions to intimate booths, from luxury brands to sustainable innovators – we honor excellence in all its forms across every category and scale.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Target className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Career Acceleration</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Our winners gain international media exposure, premium portfolio credentials, and access to an elite network of industry decision-makers and potential clients.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Trophy className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Lifetime Recognition</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    A Stand Vision Awards award is not just a trophy – it's a permanent mark of excellence that distinguishes your work throughout your entire career.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white border-2 border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <Users className="h-6 w-6 text-pink-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Community Building</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    We connect winners, finalists, and participants worldwide, fostering collaborations and friendships that transcend borders and cultures.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* The Moment That Changes Everything */}
      <section className="py-20 bg-gray-50">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
            The Moment That Changes Everything
          </h2>
          <div className="space-y-4 text-base text-gray-700 leading-relaxed">
            <p>
              Imagine standing on stage, surrounded by the world's best designers, as your name is announced as a Stand Vision Awards winner. The applause. The recognition. The validation of countless hours of hard work and creative genius.
            </p>
            <p>
              Our winners tell us this moment transforms their careers. Clients seek them out. Industry leaders want to collaborate. Their work is featured in international publications. Job offers multiply. Their confidence soars.
            </p>
            <p className="text-lg font-bold text-gray-900">
              This is what we create: life-changing recognition for deserving talent.
            </p>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6">
            Join Us in Celebrating Excellence
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-4">
            Whether you're a seasoned designer with decades of experience or an emerging talent making your mark, Stand Vision Awards is your platform to shine. We're proud to stand alongside you, championing your work and amplifying your success to the world.
          </p>
          <p className="text-base text-gray-400 mb-10">
            Because great work deserves to be celebrated. Always.
          </p>
          <Link to="/apply">
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-10 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
              data-testid="submit-project-button"
            >
              Submit Your Project
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};